import React, { useEffect, useRef, useState } from 'react';
import {
  Add as AddIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { gql, useMutation, useQuery } from 'urql';
import { useFlashMessageContext } from '../../contexts/flashMessage';
import { useToggleContext } from '../../contexts/toggle';
import {
  FetchFbsTeamsQuery,
  FetchManageableRankingSourcesQuery,
  FetchMostRecentRankingsQuery,
  FetchWeekRankingsQuery,
  SaveWeekRankingsMutation,
  SaveWeekRankingsMutationVariables,
} from '../../generated/hooks';
import { TeamAutocomplete, TeamOption } from './TeamAutocomplete';

const fetchManageableRankingSources = gql`
  query fetchManageableRankingSources {
    manageableRankingSources {
      source
      slotCount
      allowTies
    }
  }
`;

const fetchFbsTeams = gql`
  query fetchFbsTeams($season: Int!) {
    fbsTeams(season: $season) {
      id
      name
      styles {
        primaryColor
        secondaryColor
        logoComponent
      }
    }
  }
`;

const fetchMostRecentRankings = gql`
  query fetchMostRecentRankings($source: String!) {
    mostRecentRankings(source: $source) {
      id
      rank
      team {
        id
      }
    }
  }
`;

const fetchWeekRankings = gql`
  query fetchWeekRankings($week: Int!, $source: String!) {
    weekRankings(week: $week, source: $source) {
      id
    }
  }
`;

const saveWeekRankings = gql`
  mutation saveWeekRankings($input: SaveWeekRankingsInputType!) {
    saveWeekRankings(input: $input) {
      ok
      rankings {
        id
      }
    }
  }
`;

const SOURCE_LABELS: Record<string, string> = {
  AP_POLL: 'AP Poll',
  PLAYOFF: 'CFP',
  PLAYOFF_SEEDS: 'Playoff Seeds',
};
const DEFAULT_SOURCE = 'AP_POLL';

type RankingRowValue = { teamId: string; tiedWithPrevious: boolean };
type FormValues = { updateGames: boolean; rows: RankingRowValue[] };

// Skip-style: a tied row copies the rank directly above it; the row after it still gets its own
// position. Mirrors services/rankings.py::save_week_rankings exactly, for live display only.
function computeDisplayRanks(rows: RankingRowValue[]): number[] {
  const ranks: number[] = [];
  rows.forEach((row, index) => {
    ranks.push(index > 0 && row.tiedWithPrevious ? ranks[index - 1] : index + 1);
  });
  return ranks;
}

function buildRowsFromRankings(
  rankings: FetchMostRecentRankingsQuery['mostRecentRankings'],
  slotCount: number,
): RankingRowValue[] {
  if (rankings.length === 0) {
    return Array.from({ length: slotCount }, () => ({ teamId: '', tiedWithPrevious: false }));
  }
  const sorted = [...rankings].sort((a, b) => a.rank - b.rank);
  return sorted.map((ranking, index) => ({
    teamId: ranking.team.id,
    tiedWithPrevious: index > 0 && ranking.rank === sorted[index - 1].rank,
  }));
}

function AdminWeeklyRankings() {
  const { getToggle } = useToggleContext();
  const { flashMutationError } = useFlashMessageContext();
  const [confirmation, setConfirmation] = useState<string>();
  const [source, setSource] = useState(DEFAULT_SOURCE);
  const [week, setWeek] = useState('');
  const weekInitialized = useRef(false);

  const currentSeasonToggle = getToggle('currentSeason')?.value;
  const season = currentSeasonToggle ? Number(currentSeasonToggle) : undefined;
  const currentWeekToggle = getToggle('currentWeek')?.value;
  const currentWeek =
    season !== undefined && currentWeekToggle ? (JSON.parse(currentWeekToggle)[season] as number) : undefined;
  const seasonSettingsToggle = getToggle('seasonSettings')?.value;
  const lastWeek: number | undefined =
    season !== undefined && seasonSettingsToggle ? JSON.parse(seasonSettingsToggle)[season]?.lastWeek : undefined;

  useEffect(() => {
    if (!weekInitialized.current && currentWeek !== undefined) {
      setWeek(String(currentWeek));
      weekInitialized.current = true;
    }
  }, [currentWeek]);

  const [{ data: sourcesData, error: sourcesError, fetching: sourcesFetching }] =
    useQuery<FetchManageableRankingSourcesQuery>({ query: fetchManageableRankingSources });
  const sourceConfig = sourcesData?.manageableRankingSources.find(s => s.source === source);

  const [{ data: teamsData, error: teamsError, fetching: teamsFetching }] = useQuery<FetchFbsTeamsQuery>({
    query: fetchFbsTeams,
    variables: { season: season ?? 0 },
    pause: season === undefined,
  });
  const teamOptions: TeamOption[] = [...(teamsData?.fbsTeams ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  const teamsById = new Map(teamOptions.map(team => [team.id, team]));

  const [{ data: mostRecentData, fetching: mostRecentFetching }, reexecuteMostRecent] =
    useQuery<FetchMostRecentRankingsQuery>({
      query: fetchMostRecentRankings,
      variables: { source },
      pause: !source,
    });

  const [{ data: weekRankingsData }, reexecuteWeekRankings] = useQuery<FetchWeekRankingsQuery>({
    query: fetchWeekRankings,
    variables: { week: Number(week), source },
    pause: !source || week === '',
  });
  const alreadyExists = (weekRankingsData?.weekRankings.length ?? 0) > 0;

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = useForm<FormValues>({ defaultValues: { updateGames: true, rows: [] } });
  const { fields, append, remove, swap, replace } = useFieldArray({ control, name: 'rows' });

  // Pre-populate from the most recent rankings for this source whenever the source changes (including
  // the initial load). Changing the week alone doesn't touch the rows.
  useEffect(() => {
    if (mostRecentFetching || !sourceConfig) {
      return;
    }
    replace(buildRowsFromRankings(mostRecentData?.mostRecentRankings ?? [], sourceConfig.slotCount));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, mostRecentFetching, sourceConfig?.slotCount]);

  const watchedRows = useWatch({ control, name: 'rows' }) ?? [];
  const displayRanks = computeDisplayRanks(watchedRows);

  const nonEmptyTeamIds = watchedRows.map(row => row.teamId).filter(Boolean);
  const duplicateTeamIds = new Set(nonEmptyTeamIds.filter((id, index) => nonEmptyTeamIds.indexOf(id) !== index));
  const hasEmptyRow = watchedRows.some(row => !row.teamId);
  const rowCountValid = sourceConfig
    ? sourceConfig.allowTies
      ? watchedRows.length >= sourceConfig.slotCount
      : watchedRows.length === sourceConfig.slotCount
    : false;
  // Rows beyond the slot count only exist to extend a tie -- an untied one there would push the
  // displayed rank past the slot count, so every row past it must be tied with the row above.
  const hasUntiedRowPastSlotCount = sourceConfig
    ? watchedRows.slice(sourceConfig.slotCount).some(row => !row.tiedWithPrevious)
    : false;

  const blockingReasons: string[] = [];
  if (alreadyExists) {
    blockingReasons.push(`Rankings already exist for ${SOURCE_LABELS[source] ?? source} week ${week}.`);
  }
  if (hasEmptyRow) {
    blockingReasons.push('Every row needs a team selected.');
  }
  if (duplicateTeamIds.size > 0) {
    blockingReasons.push('Each team can only appear once.');
  }
  if (sourceConfig && !rowCountValid) {
    blockingReasons.push(
      sourceConfig.allowTies
        ? `At least ${sourceConfig.slotCount} rankings are required.`
        : `Exactly ${sourceConfig.slotCount} rankings are required.`,
    );
  }
  if (sourceConfig && hasUntiedRowPastSlotCount) {
    blockingReasons.push(`Rows beyond the first ${sourceConfig.slotCount} must be tied with the row above them.`);
  }

  const handleMoveUp = (index: number) => {
    if (index === 0) {
      return;
    }
    swap(index, index - 1);
    if (index - 1 === 0) {
      setValue('rows.0.tiedWithPrevious', false);
    }
  };
  const handleMoveDown = (index: number) => {
    if (index === fields.length - 1) {
      return;
    }
    swap(index, index + 1);
    if (index === 0) {
      setValue('rows.0.tiedWithPrevious', false);
    }
  };
  const handleAddRow = () => append({ teamId: '', tiedWithPrevious: true });

  const [, saveWeekRankingsMutation] = useMutation<SaveWeekRankingsMutation, SaveWeekRankingsMutationVariables>(
    saveWeekRankings,
  );
  const handleSave = handleSubmit(values => {
    if (blockingReasons.length > 0 || !sourceConfig) {
      return;
    }
    const rows = values.rows.map((row, index) => ({
      teamId: row.teamId,
      tiedWithPrevious: index === 0 ? false : row.tiedWithPrevious,
    }));
    return saveWeekRankingsMutation({
      input: { week: Number(week), source, updateGames: values.updateGames, rows },
    }).then(response => {
      if (flashMutationError(response.error)) {
        return;
      }
      setConfirmation(`Saved ${rows.length} rankings for ${SOURCE_LABELS[source] ?? source} week ${week}.`);
      reexecuteWeekRankings({ requestPolicy: 'network-only' });
      reexecuteMostRecent({ requestPolicy: 'network-only' });
    });
  });

  if (sourcesFetching && !sourcesData) {
    return (
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const error = sourcesError ?? teamsError;
  if (error) {
    return (
      <Box sx={{ width: '100%', maxWidth: 900, mx: 'auto', p: 2 }}>
        <Alert severity="error">Failed to load weekly rankings: {error.message}</Alert>
      </Box>
    );
  }

  if (season === undefined || lastWeek === undefined) {
    return (
      <Box sx={{ width: '100%', maxWidth: 900, mx: 'auto', p: 2 }}>
        <Alert severity="warning">Could not determine the current season/last week from the global toggles.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 900, mx: 'auto', p: 2 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Weekly Rankings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Enter a week&apos;s rankings for a source. Rankings for a week/source combo can only be saved once.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Source</InputLabel>
          <Select label="Source" value={source} onChange={e => setSource(e.target.value)}>
            {(sourcesData?.manageableRankingSources ?? []).map(({ source: value }) => (
              <MenuItem key={value} value={value}>
                {SOURCE_LABELS[value] ?? value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Week</InputLabel>
          <Select label="Week" value={week} onChange={e => setWeek(e.target.value)}>
            {Array.from({ length: lastWeek + 1 }, (_, w) => w).map(w => (
              <MenuItem key={w} value={String(w)}>
                Week {w}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Controller
          control={control}
          name="updateGames"
          render={({ field: { value, onChange } }) => (
            <FormControlLabel
              control={<Checkbox checked={value} onChange={e => onChange(e.target.checked)} />}
              label="Update game ranks"
            />
          )}
        />
      </Box>

      {alreadyExists && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Rankings already exist for {SOURCE_LABELS[source] ?? source} week {week}.
        </Alert>
      )}

      {teamsFetching && !teamsData ? (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <form onSubmit={handleSave}>
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 60 }}>Rank</TableCell>
                  <TableCell>Team</TableCell>
                  {sourceConfig?.allowTies && <TableCell sx={{ width: 160 }}>Tied with row above</TableCell>}
                  <TableCell sx={{ width: 140 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.map((field, index) => (
                  <TableRow key={field.id}>
                    <TableCell>{displayRanks[index]}</TableCell>
                    <TableCell>
                      <Controller
                        control={control}
                        name={`rows.${index}.teamId`}
                        render={({ field: { value, onChange } }) => (
                          <TeamAutocomplete
                            options={teamOptions}
                            value={teamsById.get(value) ?? null}
                            onChange={team => onChange(team?.id ?? '')}
                          />
                        )}
                      />
                      {duplicateTeamIds.has(watchedRows[index]?.teamId) && watchedRows[index]?.teamId && (
                        <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                          This team is ranked more than once.
                        </Typography>
                      )}
                    </TableCell>
                    {sourceConfig?.allowTies && (
                      <TableCell>
                        {index > 0 && (
                          <Controller
                            control={control}
                            name={`rows.${index}.tiedWithPrevious`}
                            render={({ field: { value, onChange } }) => (
                              <Checkbox checked={value} onChange={e => onChange(e.target.checked)} />
                            )}
                          />
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <IconButton size="small" disabled={index === 0} onClick={() => handleMoveUp(index)}>
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={index === fields.length - 1}
                        onClick={() => handleMoveDown(index)}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                      {sourceConfig?.allowTies && fields.length > sourceConfig.slotCount && (
                        <IconButton size="small" onClick={() => remove(index)}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {sourceConfig?.allowTies && (
            <Button startIcon={<AddIcon />} onClick={handleAddRow} sx={{ mb: 2 }}>
              Add row for a tie
            </Button>
          )}

          {blockingReasons.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {blockingReasons.map(reason => (
                <Alert key={reason} severity="warning" sx={{ mb: 1 }}>
                  {reason}
                </Alert>
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button variant="contained" type="submit" disabled={isSubmitting || blockingReasons.length > 0}>
              {isSubmitting ? 'Saving…' : 'Save Rankings'}
            </Button>
            {confirmation && (
              <Typography variant="body2" color="success.main">
                {confirmation}
              </Typography>
            )}
          </Box>
        </form>
      )}
    </Box>
  );
}

export default AdminWeeklyRankings;
